import { isElement } from './dom.js';
import { toType } from './types.js';

/**
 * @param {{ NAME: any; jQueryInterface: any; }} plugin
 */
export const defineJQueryPlugin = (plugin) => {
	$(() => {
		/** @type {string} */
		const name = plugin.NAME;
		/** @type {any} */
		const JQUERY_NO_CONFLICT = $.fn[name];
		/** @type {any} */
		$.fn[name] = plugin.jQueryInterface;
		$.fn[name].Constructor = plugin;
		$.fn[name].noConflict = () => {
			$.fn[name] = JQUERY_NO_CONFLICT;
			return plugin.jQueryInterface;
		};
	});
};

/**
 *
 * @param {string} componentName
 * @param {{ [key: string]: any; }} config
 * @param {{ [key: string]: string; }} configTypes
 * @param {{ [key: string]: string|boolean; }=} deprecatedConfigTypes
 */
export const typeCheckConfig = (componentName, config, configTypes, deprecatedConfigTypes) => {
	for (const [property, expectedTypes] of Object.entries(configTypes)) {
		const value = config[property];
		const valueType = value && isElement(value) ? 'element' : (value == null ? 'undefined' : toType(value));

		if ((value != null) && !new RegExp(expectedTypes).test(valueType)) {
			throw new TypeError(
				`${componentName.toUpperCase()}: Option "${property}" provided type "${valueType}" but expected type "${expectedTypes}".`,
			);
		}
	}

	if (deprecatedConfigTypes) {
		for (const [property, newPropertyOrTrue] of Object.entries(deprecatedConfigTypes)) {
			if (config[property] !== undefined) {
				let msg = `${componentName.toUpperCase()}: Option "${property}" is deprecated.`;
				let args = [];
				if (typeof newPropertyOrTrue === 'string') {
					msg += ` Use "${newPropertyOrTrue}" instead.`;

					// New property is also in the config object
					if (newPropertyOrTrue in config) {
						msg += ' "%o = %o" will override "%o = %o".';
						args = [newPropertyOrTrue, config[newPropertyOrTrue], property, config[property]];
						config[property] = config[newPropertyOrTrue];
					}
				}
				console.warn(msg, ...args);
			}
		}
	}
};

/**
 *
 * @param {string} componentType
 * @param {*} options
 * @param {JQuery.TriggeredEvent=} e
 */
export const applyComponentClickRestrictions = (componentType, options, e) => {

	// Don't do anything on mobile
	if (((options.mobile === false) || (options.disableMobile === true)) && (mpro.isMobileScreen() === true)) {
		return false;
	}

	// Don't do anything on desktop
	if (((options.desktop === false) || (options.disableDesktop === true)) && (mpro.isMobileScreen() === false)) {
		return false;
	}

	// Don't do anything if we have a guest
	if ((options.disableGuest === true) && mpro.isGuest()) {
		return false;
	}

	if (typeof e !== 'undefined') {
		// Continue as normal for cmd clicks etc
		if ((e.which > 1 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) && // Exclude some components
			['confirm', 'alert', 'ask', 'dialog'].includes(componentType) === false) {

			/** @type {HTMLElement} */
			const element = e.currentTarget;
			let isClickable = false;

			if (
				// @ts-ignore
				// Anchor with href
				(element.href && !element.href.startsWith('#') && !element.href.startsWith('javascript')) ||
				element.dataset.url
			) {
				isClickable = true;
			}

			// Allow command clicks
			if (isClickable) {
				return false;
			}
		}

		// Disable all other event handlers
		e.preventDefault();
		e.stopImmediatePropagation();
		e.stopPropagation();
	}

	return true;
};

/**
 * Merge the breakpoints of the matched media into the main options
 *
 * @param {*} options
 * @param {string} bpName
 */
export const applyBreakpoints = (options, bpName = 'breakpoints') => {

	if (options[bpName] && window.matchMedia) {
		const map = Object.keys(options[bpName])
			.sort((n, m) => Number(n) - Number(m))
			.map(point => ({ point, mql: window.matchMedia(`(max-width:${point}px)`) }));

		const item = map.find(item => item.mql.matches);

		if (item) {
			return { ...options, ...options[bpName][item.point] };
		}
	}

	return options;
};